/**
 * Finanzas → **Kardex Valorizado** (`/finanzas/kardex-valorizado`).
 *
 * Puerto de `Inventory::kardex_valorizado` con la vista
 * `views/inventory/kardex_valorizado.php`. Es el **FORMATO 13.1** de la SUNAT
 * (*Registro de Inventario Permanente Valorizado*): de un producto y un mes, el
 * documento, el tipo de operación, las entradas, las salidas y el saldo final
 * valorizado a costo promedio.
 *
 * Reglas heredadas:
 * - El **saldo inicial** sale del cierre del mes anterior; si no hay cierre, se usa la
 *   cantidad del tanque (la última medición física ajustada) sin costo, y la pantalla
 *   lo avisa.
 * - Dentro de cada día se procesan primero las compras (con su **flete** y sus **notas
 *   de crédito/débito**, cada uno en su fila) y al final las ventas.
 * - El **cierre de mes** guarda el saldo final del período y no se puede hacer con
 *   cantidad negativa.
 *
 * Diferencias anotadas:
 * - El original **escribía el cierre de mes pero nunca lo leía**: el saldo inicial
 *   salía siempre con costo 0 y el valorizado arrancaba de cero. Ahora el cierre es la
 *   primera fuente del saldo inicial, y los saldos valorizados son reales.
 * - Los totales del período y el aviso de «sin cierre del mes anterior» (o de «ya
 *   cerrado») están en la cabecera, en lugar del aviso emergente del original.
 */

import { Download, FileSpreadsheet, Info, Lock, Search } from 'lucide-react';
import { useMemo, useState } from 'react';

import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { CampoFecha } from '@/components/ui/calendario';
import { Paginador } from '@/components/ui/paginador';
import { Select } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { usePaginacion } from '@/hooks/use-paginacion';
import { abrirPdfAutenticado } from '@/lib/descargas';
import { ApiError } from '@/lib/http/api-error';
import { cn, formatCurrency } from '@/lib/utils';

import { CierreMesDialog } from '../components/cierre-mes-dialog';
import {
  useFormatoValorizado,
  useProductosValorizado,
  valorizadoApi,
} from '../kardex-valorizado.api';
import type { FiltrosValorizado } from '../kardex-valorizado.api';

/** Clave de la preferencia de ancho de columnas del formato (no se guarda nada más). */
const CONTROL =
  'h-10 w-full rounded-control border border-ink-200 bg-white px-3 text-sm text-ink-900 ' +
  'transition-colors focus:border-brand-400 focus:ring-4 focus:ring-brand-100 focus:outline-none';

/** El mes en curso en `AAAA-MM`. */
function mesActual(): string {
  const hoy = new Date();

  return `${hoy.getFullYear()}-${String(hoy.getMonth() + 1).padStart(2, '0')}`;
}

/** Primer y último día de un mes `AAAA-MM`. */
function rangoDelMes(mes: string): { desde: string; hasta: string } {
  const anio = Number(mes.slice(0, 4));
  const numeroMes = Number(mes.slice(5, 7));

  if (!Number.isInteger(anio) || !Number.isInteger(numeroMes)) {
    return { desde: '', hasta: '' };
  }

  const ultimo = new Date(anio, numeroMes, 0).getDate();

  return {
    desde: `${mes}-01`,
    hasta: `${mes}-${String(ultimo).padStart(2, '0')}`,
  };
}

/** Cantidad con seis decimales, como el formato de la SUNAT. */
function seisDecimales(valor: number | '' | null | undefined): string {
  if (valor === '' || valor === null || valor === undefined) return '—';

  const numero = Number(valor);

  return Number.isFinite(numero)
    ? numero.toLocaleString('es-PE', { minimumFractionDigits: 6, maximumFractionDigits: 6 })
    : '—';
}

/** Etiqueta del tipo de operación, con su color. */
function tonoDeOperacion(operacion: string): string {
  if (operacion === 'COMPRA') return 'bg-emerald-50 text-emerald-700';
  if (operacion === 'FLETE') return 'bg-sky-50 text-sky-700';
  if (operacion === 'VENTA') return 'bg-rose-50 text-rose-700';

  return 'bg-amber-50 text-amber-700';
}

export function KardexValorizadoPage() {
  useDocumentTitle('Finanzas · Kardex valorizado');

  const [productoId, setProductoId] = useState('');
  const [mes, setMes] = useState(mesActual);
  const [filtros, setFiltros] = useState<FiltrosValorizado | null>(null);
  const [dialogo, setDialogo] = useState(false);
  const [aviso, setAviso] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [descargando, setDescargando] = useState(false);

  const productos = useProductosValorizado();
  const formato = useFormatoValorizado(filtros);

  const lista = useMemo(() => productos.data?.productos ?? [], [productos.data]);
  const elegido = useMemo(
    () => lista.find((producto) => String(producto.id) === productoId) ?? lista[0],
    [lista, productoId],
  );

  const consultar = () => {
    setError(null);
    setAviso(null);

    if (!elegido) {
      setError('No hay productos configurados para el kardex.');
      return;
    }

    const { desde, hasta } = rangoDelMes(mes);

    setFiltros({ product: elegido.id, startDate: desde, endDate: hasta });
  };

  const datos = formato.data;

  // Un mes de un producto puede traer cientos de movimientos: se pagina **en el
  // navegador**. La fila del saldo inicial queda anclada arriba de la tabla y los
  // totales del pie siguen saliendo del listado completo, no de la página visible.
  const filas = useMemo(() => datos?.filas ?? [], [datos]);
  const paginacion = usePaginacion(filas, { clave: filtros });

  const exportar = async () => {
    if (!filtros) return;

    setError(null);
    setDescargando(true);

    try {
      await abrirPdfAutenticado(valorizadoApi.urlExcel(filtros), {
        nombre: `formato_13_1_${filtros.startDate.slice(0, 7).replace('-', '')}.xlsx`,
      });
    } catch (fallo) {
      setError(
        fallo instanceof ApiError
          ? fallo.message
          : fallo instanceof Error
            ? fallo.message
            : 'No se pudo exportar el formato.',
      );
    } finally {
      setDescargando(false);
    }
  };

  /** Totales del período (sin contar la fila de saldo inicial). */
  const totales = filas.reduce(
    (acumulado, fila) => ({
      entradasCantidad: acumulado.entradasCantidad + Number(fila.cantidadEntrada || 0),
      entradasTotal: acumulado.entradasTotal + Number(fila.costoTotalEntrada || 0),
      salidasCantidad: acumulado.salidasCantidad + Number(fila.cantidadSalida || 0),
      salidasTotal: acumulado.salidasTotal + Number(fila.costoTotalSalida || 0),
    }),
    { entradasCantidad: 0, entradasTotal: 0, salidasCantidad: 0, salidasTotal: 0 },
  );

  const mesDeFiltros = useMemo(() => {
    const anioFiltro = Number(mes.slice(0, 4));
    const mesFiltro = Number(mes.slice(5, 7));

    return {
      mes: Number.isInteger(mesFiltro) && mesFiltro >= 1 ? mesFiltro : 1,
      anio: Number.isInteger(anioFiltro) ? anioFiltro : new Date().getFullYear(),
    };
  }, [mes]);

  const sinCierreAnterior = datos?.saldoInicial.origen === 'medicion';

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-ink-900">Kardex valorizado</h1>
          <p className="mt-1 text-sm text-ink-500">
            Formato 13.1 de la SUNAT: el inventario permanente valorizado de un producto en el mes,
            a costo promedio.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            leftIcon={<Download className="size-4" aria-hidden="true" />}
            disabled={!filtros}
            loading={descargando}
            onClick={() => void exportar()}
          >
            Descargar Excel
          </Button>
          <Button
            leftIcon={<Lock className="size-4" aria-hidden="true" />}
            disabled={!datos || datos.cierre?.cerrado === true}
            onClick={() => setDialogo(true)}
          >
            Cerrar mes
          </Button>
        </div>
      </header>

      {error ? (
        <Alert variant="error" title="Revisa los filtros">
          {error}
        </Alert>
      ) : null}

      {aviso ? <Alert variant="success">{aviso}</Alert> : null}

      {/* Filtros */}
      <Card>
        <CardContent className="grid gap-3 p-4 sm:grid-cols-2 xl:grid-cols-4">
          <label className="flex flex-col gap-1 text-xs font-medium text-ink-500">
            Producto
            <Select
              value={elegido ? String(elegido.id) : ''}
              onChange={(evento) => setProductoId(evento.target.value)}
              className={CONTROL}
              aria-label="Producto"
            >
              {lista.length === 0 ? <option value="">Sin productos</option> : null}
              {lista.map((producto) => (
                <option key={producto.id} value={String(producto.id)}>
                  {producto.nombre}
                </option>
              ))}
            </Select>
          </label>

          <label className="flex flex-col gap-1 text-xs font-medium text-ink-500">
            Mes y año
            <CampoFecha
              tipo="month"
              value={mes}
              max={mesActual()}
              onChange={(evento) => setMes(evento.target.value)}
              aria-label="Mes y año"
            />
          </label>

          <div className="flex items-end gap-2 sm:col-span-2">
            <Button leftIcon={<Search className="size-4" aria-hidden="true" />} onClick={consultar}>
              Consultar
            </Button>
            {datos ? (
              <span className="text-xs text-ink-500">
                {datos.producto} · {datos.desde} a {datos.hasta} ·{' '}
                {filas.length.toLocaleString('es-PE')} movimiento(s)
              </span>
            ) : null}
          </div>
        </CardContent>
      </Card>

      {/* Avisos del período */}
      {datos && sinCierreAnterior ? (
        <Alert variant="warning" title="No hay cierre del mes anterior">
          El saldo inicial ({seisDecimales(datos.saldoInicial.cantidad)}) es la cantidad del tanque
          sin costo, así que el costo promedio del período arranca en cero. Cierra el mes{' '}
          {datos.saldoInicial.mes} para que el siguiente kardex salga valorizado.
        </Alert>
      ) : null}

      {datos?.cierre?.cerrado ? (
        <Alert variant="info" title={`El mes ${String(mesDeFiltros.mes).padStart(2, '0')}/${mesDeFiltros.anio} ya está cerrado`}>
          Se cerró con {seisDecimales(datos.cierre.cantidad)} a un costo unitario de{' '}
          {seisDecimales(datos.cierre.precio)} ({datos.cierre.creado}
          {datos.cierre.comentario ? ` · ${datos.cierre.comentario}` : ''}).
        </Alert>
      ) : null}

      {/* Contadores */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {[
          { etiqueta: 'Saldo inicial', valor: seisDecimales(datos?.saldoInicial.cantidad), tono: 'text-ink-900' },
          {
            etiqueta: 'Entradas del mes',
            valor: seisDecimales(totales.entradasCantidad),
            tono: 'text-emerald-700',
          },
          {
            etiqueta: 'Salidas del mes',
            valor: seisDecimales(totales.salidasCantidad),
            tono: 'text-amber-700',
          },
          {
            etiqueta: 'Saldo final valorizado',
            valor: datos ? formatCurrency(datos.saldoFinal.total) : formatCurrency(0),
            tono: 'text-sky-700',
          },
        ].map((tarjeta) => (
          <Card key={tarjeta.etiqueta}>
            <CardContent className="flex flex-col gap-0.5 p-4">
              <p className="text-[11px] font-semibold tracking-wide text-ink-500 uppercase">
                {tarjeta.etiqueta}
              </p>
              <p className={cn('text-base font-bold break-words tabular-nums sm:text-lg', tarjeta.tono)}>
                {tarjeta.valor}
              </p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Formato 13.1 */}
      <Card>
        <CardContent className="space-y-3 p-4">
          {formato.isError ? (
            <Alert variant="error" title="No se pudo armar el formato">
              {formato.error instanceof ApiError
                ? formato.error.message
                : 'Inténtalo de nuevo en unos segundos.'}
            </Alert>
          ) : null}

          <div className="overflow-auto rounded-card border border-ink-100">
            <table className="w-full border-collapse text-[12px]">
              <thead className="bg-ink-50/70 text-center text-[10px] font-semibold tracking-wide text-ink-600 uppercase">
                <tr>
                  <th colSpan={4} className="border-b border-ink-200 px-2 py-1">
                    Documento de traslado, comprobante de pago, documento interno o similar
                  </th>
                  <th rowSpan={2} className="border-b border-l border-ink-200 px-2 py-1">
                    Tipo de operación (tabla 12)
                  </th>
                  <th colSpan={3} className="border-b border-l border-ink-200 px-2 py-1 text-emerald-700">
                    Entradas
                  </th>
                  <th colSpan={3} className="border-b border-l border-ink-200 px-2 py-1 text-amber-700">
                    Salidas
                  </th>
                  <th colSpan={3} className="border-b border-l border-ink-200 px-2 py-1 text-sky-700">
                    Saldo final
                  </th>
                </tr>
                <tr>
                  {['Fecha', 'Tipo (tabla 10)', 'Serie', 'Número'].map((titulo) => (
                    <th key={titulo} className="px-2 py-1 whitespace-nowrap">
                      {titulo}
                    </th>
                  ))}
                  {['Cantidad', 'Costo unitario', 'Costo total', 'Cantidad', 'Costo unitario', 'Costo total', 'Cantidad', 'Costo unitario', 'Costo total'].map(
                    (titulo, indice) => (
                      <th
                        key={`${titulo}-${indice}`}
                        className={cn(
                          'px-2 py-1 whitespace-nowrap',
                          [0, 3, 6].includes(indice) && 'border-l border-ink-200',
                        )}
                      >
                        {titulo}
                      </th>
                    ),
                  )}
                </tr>
              </thead>
              <tbody>
                {formato.isLoading
                  ? Array.from({ length: 6 }).map((_, fila) => (
                      <tr key={fila} className="border-t border-ink-100">
                        {Array.from({ length: 14 }).map((__, columna) => (
                          <td key={columna} className="px-2 py-3">
                            <Skeleton className="h-4 w-full" />
                          </td>
                        ))}
                      </tr>
                    ))
                  : (
                      <>
                        {datos &&
                        (datos.saldoInicial.cantidad !== 0 || datos.saldoInicial.total !== 0) ? (
                          <tr className="border-t border-ink-100 bg-ink-50/70 font-semibold">
                            <td className="px-2 py-1.5" colSpan={4} />
                            <td className="border-l border-ink-100 px-2 py-1.5 text-center">
                              SALDO INICIAL
                            </td>
                            <td className="border-l border-ink-100 px-2 py-1.5 text-right tabular-nums" colSpan={3} />
                            <td className="border-l border-ink-100 px-2 py-1.5 text-right tabular-nums" colSpan={3} />
                            <td className="border-l border-ink-100 px-2 py-1.5 text-right tabular-nums">
                              {seisDecimales(datos.saldoInicial.cantidad)}
                            </td>
                            <td className="px-2 py-1.5 text-right tabular-nums">
                              {seisDecimales(datos.saldoInicial.precio)}
                            </td>
                            <td className="px-2 py-1.5 text-right tabular-nums">
                              {seisDecimales(datos.saldoInicial.total)}
                            </td>
                          </tr>
                        ) : null}

                        {paginacion.visibles.map((fila, indice) => (
                          <tr key={`${fila.fecha}-${fila.operacion}-${indice}`} className="border-t border-ink-100">
                            <td className="px-2 py-1.5 whitespace-nowrap">{fila.fecha}</td>
                            <td className="px-2 py-1.5 text-center tabular-nums">
                              {fila.tipoDocumento || '—'}
                            </td>
                            <td className="px-2 py-1.5 whitespace-nowrap">{fila.serie || '—'}</td>
                            <td className="px-2 py-1.5 whitespace-nowrap">{fila.numero || '—'}</td>
                            <td className="border-l border-ink-100 px-2 py-1.5 text-center">
                              <span
                                className={cn(
                                  'rounded-full px-2 py-0.5 text-[10px] font-semibold whitespace-nowrap',
                                  tonoDeOperacion(fila.operacion),
                                )}
                              >
                                {fila.operacion === 'out' ? 'SALIDA' : fila.operacion}
                              </span>
                            </td>
                            <td className="border-l border-ink-100 px-2 py-1.5 text-right tabular-nums">
                              {seisDecimales(fila.cantidadEntrada)}
                            </td>
                            <td className="px-2 py-1.5 text-right tabular-nums">
                              {seisDecimales(fila.costoUnitarioEntrada)}
                            </td>
                            <td className="px-2 py-1.5 text-right tabular-nums">
                              {seisDecimales(fila.costoTotalEntrada)}
                            </td>
                            <td className="border-l border-ink-100 px-2 py-1.5 text-right tabular-nums">
                              {seisDecimales(fila.cantidadSalida)}
                            </td>
                            <td className="px-2 py-1.5 text-right tabular-nums">
                              {seisDecimales(fila.costoUnitarioSalida)}
                            </td>
                            <td className="px-2 py-1.5 text-right tabular-nums">
                              {seisDecimales(fila.costoTotalSalida)}
                            </td>
                            <td className="border-l border-ink-100 px-2 py-1.5 text-right font-semibold tabular-nums">
                              {seisDecimales(fila.saldoCantidad)}
                            </td>
                            <td className="px-2 py-1.5 text-right tabular-nums">
                              {seisDecimales(fila.saldoCostoUnitario)}
                            </td>
                            <td className="px-2 py-1.5 text-right font-semibold tabular-nums">
                              {seisDecimales(fila.saldoCostoTotal)}
                            </td>
                          </tr>
                        ))}
                      </>
                    )}
              </tbody>
              {filas.length > 0 ? (
                <tfoot>
                  <tr className="border-t border-ink-200 bg-ink-50/60 text-[12px] font-semibold text-ink-700">
                    <td className="px-2 py-2" colSpan={4}>
                      Totales del período
                    </td>
                    <td className="border-l border-ink-200 px-2 py-2" />
                    <td className="border-l border-ink-200 px-2 py-2 text-right tabular-nums">
                      {seisDecimales(totales.entradasCantidad)}
                    </td>
                    <td className="px-2 py-2" />
                    <td className="px-2 py-2 text-right tabular-nums">
                      {seisDecimales(totales.entradasTotal)}
                    </td>
                    <td className="border-l border-ink-200 px-2 py-2 text-right tabular-nums">
                      {seisDecimales(totales.salidasCantidad)}
                    </td>
                    <td className="px-2 py-2" />
                    <td className="px-2 py-2 text-right tabular-nums">
                      {seisDecimales(totales.salidasTotal)}
                    </td>
                    <td className="border-l border-ink-200 px-2 py-2 text-right tabular-nums">
                      {seisDecimales(datos?.saldoFinal.cantidad)}
                    </td>
                    <td className="px-2 py-2 text-right tabular-nums">
                      {seisDecimales(datos?.saldoFinal.precio)}
                    </td>
                    <td className="px-2 py-2 text-right tabular-nums">
                      {seisDecimales(datos?.saldoFinal.total)}
                    </td>
                  </tr>
                </tfoot>
              ) : null}
            </table>
          </div>

          <Paginador {...paginacion} etiqueta="movimientos" />

          {!formato.isLoading && filtros === null ? (
            <p className="text-sm text-ink-500">
              Elige el producto y el mes, y pulsa <em>Consultar</em>.
            </p>
          ) : null}

          {!formato.isLoading && filtros !== null && filas.length === 0 ? (
            <Alert variant="warning" title="Sin movimientos en el mes">
              Ese producto no tiene compras, ventas ni salidas en el mes elegido. El saldo inicial y
              el final son el mismo.
            </Alert>
          ) : null}

          <p className="flex items-start gap-2 text-xs text-ink-500">
            <Info className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
            El costo unitario del saldo se recalcula con cada entrada (promedio ponderado); las
            ventas y las salidas de almacén se valorizan a ese costo. El **flete** de la compra y sus
            **notas de crédito o débito** van en su propia fila y ajustan el costo total.
          </p>
        </CardContent>
      </Card>

      <p className="flex items-start gap-2 text-xs text-ink-500">
        <FileSpreadsheet className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
        El Excel sale con el encabezado del **FORMATO 13.1** (período, RUC, razón social,
        establecimiento, producto y método de valuación) y las mismas filas que la tabla.
      </p>

      {datos ? (
        <CierreMesDialog
          open={dialogo}
          onClose={() => setDialogo(false)}
          datos={datos}
          mes={mesDeFiltros.mes}
          anio={mesDeFiltros.anio}
          onAviso={(mensaje) => {
            setAviso(mensaje);
            setFiltros(null);
          }}
        />
      ) : null}
    </div>
  );
}
